/**
 * `/s/:code` — the one route that matters.
 *
 * The order of things, and the order is the point:
 *
 *   1. no slot held → join screen, or "this one is taken", or "this session has ended"
 *   2. slot held → the socket opens immediately, with no sensors and no prompts
 *   3. made this session and nobody here yet → the share screen, so the link goes out *first*
 *   4. somebody is here (or you are the one who was invited) → the two permission taps
 *   5. then the arrow
 *
 * Steps 2–4 used to be one step in the wrong order: onboarding ran before anything else, so the
 * person holding a link nobody had received yet had to grant location and compass before they
 * could send it — and if they denied location they landed on a screen with no way forward at
 * all, still holding the unsent link. Sharing a URL needs no sensors, so it no longer waits for
 * any.
 *
 * Above all of it sits the session bar (`ui/session-bar.ts`): the link and the clock, which used
 * to vanish the instant somebody joined.
 */

import { deviceClassOf, SessionMetrics } from '@beeline/shared';

import { api, ApiError } from '../../lib/api';
import { RALLY_SUGGESTIONS } from '../../lib/destination';
import { el } from '../../lib/dom';
import { recallMembership, refreshMembershipExpiry, type Membership } from '../../lib/membership';
import { savePin } from '../../lib/pins';
import type { View } from '../../lib/router';
import {
  SessionModel,
  defaultFocus,
  otherMembers,
  type SessionState,
} from '../../lib/session-model';
import { SessionSocket } from '../../lib/socket';
import { SensorHub, type SensorState } from '../../sensors';
import { errorScreen, spinner } from '../../ui/feedback';
import { installSuggestion } from '../../ui/install-offer';
import { invitePanel } from '../../ui/invite';
import { createSessionBar, type SessionBar } from '../../ui/session-bar';
import { createSheet, type Sheet } from '../../ui/sheet';
import { convergedScreen } from './converged';
import { endedScreen, fullScreen } from './ended';
import { joinScreen } from './join';
import { liveScreen, type LiveScreen } from './live';
import { mountOnboarding } from './onboarding';
import { shareScreen, type ShareScreen } from './share';
import { placePicker, type PlacePicker } from '../place';
import { readSimOptions, startSimulatedPeer, type SimulatedPeer } from '../../sim/peer';

type Mounted = 'none' | 'share' | 'live' | 'onboarding' | 'terminal' | 'picker';

export const sessionView: View = (container, context) => {
  const code = (context.params['code'] ?? '').toUpperCase();
  /** `?sim=1` spawns a fake peer *and* hands the keyboard the wheel, so one browser is enough. */
  const sim = readSimOptions();
  const simulate = sim !== null;

  let disposed = false;
  let socket: SessionSocket | null = null;
  let model: SessionModel | null = null;
  let sensors: SensorHub | null = null;
  let simPeer: SimulatedPeer | null = null;
  let unsubscribe: (() => void) | null = null;
  let stopOnboarding: (() => void) | null = null;
  let share: ShareScreen | null = null;
  let live: LiveScreen | null = null;
  let bar: SessionBar | null = null;
  let sheet: Sheet | null = null;
  let mounted: Mounted = 'none';
  let rallyPicker: PlacePicker | null = null;

  /**
   * True once the two permission taps have happened — *however* they went.
   *
   * A refusal is an answer. It used to be a dead end, which meant a denied location took the
   * whole session with it; now the screens simply say what is missing, which is the honest
   * behaviour the product claims everywhere else.
   */
  let sensorsReady = false;
  /** This device created the session, rather than following a link into it. */
  let host = false;

  /** The last of each, so either arriving can re-render the screen. */
  let sessionState: SessionState | null = null;
  let sensorState: SensorState | null = null;
  /** Set once, by either side's own proximity check. There is no un-meeting. */
  let converged = false;

  /**
   * This device's anonymous account of the session, accumulated as it goes and sent once at the
   * end. It holds counts and distances, never a position.
   */
  const metrics = new SessionMetrics(deviceClassOf(navigator.userAgent));
  let telemetrySent = false;

  const reportTelemetry = (): void => {
    if (telemetrySent || !metrics.isWorthReporting) return;
    telemetrySent = socket?.sendTelemetry(metrics.summarize(Date.now())) ?? false;
  };

  // Most sessions end by someone walking away from their phone, not by tapping anything, so
  // the summary also goes out when the page is hidden.
  const onHidden = (): void => {
    if (document.visibilityState === 'hidden') reportTelemetry();
  };
  document.addEventListener('visibilitychange', onHidden);

  /* --- the shell -------------------------------------------------------- */

  /**
   * The bar and the view under it, as one persistent frame.
   *
   * Screens swap inside the slot; the bar is built once and outlives all of them, which is the
   * entire reason "invite one more person" and "add an hour" are now possible at all.
   */
  const slot = el('div', { class: 'session__view' });
  const shell = el('div', { class: 'session' });

  const teardownLive = (): void => {
    unsubscribe?.();
    unsubscribe = null;
    stopOnboarding?.();
    stopOnboarding = null;
    socket?.close();
    socket = null;
    model?.stop();
    model = null;
    sensors?.stop();
    sensors = null;
    simPeer?.stop();
    simPeer = null;
    share?.destroy();
    share = null;
    live?.destroy();
    live = null;
    sheet?.destroy();
    sheet = null;
    bar?.destroy();
    bar = null;
    rallyPicker?.destroy();
    rallyPicker = null;
  };

  /** Mount a screen. Everything except a terminal screen keeps the bar above it. */
  const mount = (node: HTMLElement, kind: Mounted): void => {
    if (disposed) return;
    if (kind !== 'share') {
      share?.destroy();
      share = null;
    }
    if (kind !== 'live') {
      live?.destroy();
      live = null;
    }
    mounted = kind;

    if (kind === 'terminal' || kind === 'picker' || !bar) {
      container.replaceChildren(node);
      return;
    }

    shell.replaceChildren(bar.element, slot);
    slot.replaceChildren(node);
    if (container.firstChild !== shell) container.replaceChildren(shell);
  };

  const showTerminal = (node: HTMLElement): void => {
    teardownLive();
    mount(node, 'terminal');
  };

  const showLoading = (message: string): void => {
    container.replaceChildren(
      el(
        'main',
        { class: 'screen screen--centered' },
        spinner(),
        el('p', { class: 'screen__body', text: message }),
      ),
    );
  };

  /* --- inviting, at any point in the session ----------------------------- */

  const closeSheet = (): void => {
    sheet?.destroy();
    sheet = null;
  };

  const showInvite = (): void => {
    closeSheet();
    const panel = invitePanel({ url: `${window.location.origin}/s/${code}` });
    const opened = createSheet({
      title: 'Invite somebody',
      body:
        sessionState && sessionState.capacity > 2
          ? `Room for ${Math.max(0, sessionState.capacity - sessionState.members.length)} more.`
          : 'The same link works for anyone who has not joined yet.',
      content: panel.element,
      onClose: () => {
        panel.destroy();
        closeSheet();
      },
    });
    sheet = opened;
    container.append(opened.element);
  };

  /* --- the rally point -------------------------------------------------- */

  /**
   * Choose the one place the whole room walks to.
   *
   * The map takes over the whole screen while it is open — whatever was under it is torn down to
   * nothing but a paused socket, because a WebGL arrow rendering behind a map is a battery bill
   * nobody asked for. Confirming sends it to the room; every arrow swings at once.
   *
   * Reachable from the share screen as well as the live one, which is the fix for a genuinely
   * silly ordering: the feature a group session exists for could not be used until somebody had
   * already joined and started walking at a person instead.
   */
  const showRallyPicker = (): void => {
    rallyPicker?.destroy();
    closeSheet();

    // Actually tear the live screen down, rather than merely covering it. Detaching its element
    // would leave its one-second ticker and its WebGL context alive behind the map, and the
    // rebuild on close would orphan them for good — a leaked renderer per pin dropped.
    live?.destroy();
    live = null;

    const close = (): void => {
      rallyPicker?.destroy();
      rallyPicker = null;
      // Force a rebuild of whatever the picker replaced in the container.
      mounted = 'none';
      render();
    };

    const chooser = placePicker({
      title: 'Where is everyone going?',
      body: 'Drag the map so the crosshair is on the meeting point. Everybody’s arrow swings to it.',
      confirmLabel: 'Send it to everyone',
      defaultLabel: 'Meeting point',
      suggestions: RALLY_SUGGESTIONS,
      start: sessionState?.rally
        ? { lat: sessionState.rally.lat, lng: sessionState.rally.lng }
        : null,
      onConfirm: (destination) => {
        socket?.sendDestination({
          lat: destination.lat,
          lng: destination.lng,
          label: destination.label,
          photo: destination.photo ?? null,
        });
        // Shown at once; the server's broadcast overwrites this a moment later with `by` and
        // `at` filled in, which is the version everybody else is looking at.
        model?.setRally({
          lat: destination.lat,
          lng: destination.lng,
          label: destination.label,
          by: sessionState?.you ?? 'a',
          at: Date.now(),
          photo: destination.photo ?? null,
        });
        close();
      },
      onCancel: close,
    });

    rallyPicker = chooser;
    container.replaceChildren(chooser.element);
    mounted = 'picker';
  };

  /** Put every arrow back on a person. Plans change, and so does the wrong gate. */
  const clearRally = (): void => {
    socket?.sendDestination(null);
    model?.setRally(null);
  };

  /* --- the connected state ---------------------------------------------- */

  const render = (): void => {
    // The map picker owns the screen while it is open; nothing may paint over it.
    if (mounted === 'picker') return;
    if (disposed || !sessionState) return;
    const state = sessionState;

    bar?.update(state);

    if (state.ended) {
      showTerminal(state.ended === 'refused' ? fullScreen() : endedScreen());
      return;
    }

    if (state.expiresAt) refreshMembershipExpiry(code, state.expiresAt);

    const others = otherMembers(state);

    if (converged) {
      // The work is done and the group is looking at each other, not at a phone.
      if (mounted !== 'terminal') {
        const fix = sensorState?.fix;
        teardownLive();
        mount(
          // The one moment the product has just proven itself, and the only place it asks.
          convergedScreen({
            peerName: others[0]?.name ?? null,
            install: installSuggestion(),
            groupSize: state.members.length,
            usedRallyPoint: state.rally !== null,
            // Offered only when there is something honest to save. A pin made from a fix we
            // would not point an arrow with would send somebody to the wrong place later.
            onPinThisSpot:
              fix?.usable === true
                ? () => {
                    savePin({
                      lat: fix.lat,
                      lng: fix.lng,
                      label: 'Where we met',
                      pinnedAt: Date.now(),
                      accuracy: fix.accuracy,
                      altitude: fix.altitude,
                    });
                  }
                : null,
          }),
          'terminal',
        );
      }
      return;
    }

    /*
     * The permission taps, at the moment they are actually needed.
     *
     * Somebody is here, or this device followed a link in rather than handing one out — either
     * way the next thing on screen is an arrow, and an arrow needs sensors. A host alone with an
     * unsent link needs nothing of the kind, and asking them first is how the link went unsent.
     */
    const arrowIsNext = !host || others.some((member) => member.present || member.frame);
    if (!sensorsReady) {
      if (arrowIsNext) {
        if (mounted !== 'onboarding') showOnboarding();
        return;
      }
      showShare(state);
      return;
    }

    if (arrowIsNext) {
      if (mounted !== 'live' || !live) {
        const screen = liveScreen({
          code,
          onChooseRole: (role) => {
            socket?.sendRole(role);
            model?.setRole(role);
          },
          onChooseRally: () => showRallyPicker(),
          onClearRally: () => clearRally(),
          onSignal: (signal) => socket?.sendSignal(signal),
          onConverged: () => {
            converged = true;
            metrics.markConverged(Date.now());
            // Tell them too: their own check will agree, but this makes it instant.
            socket?.sendConverged();
            reportTelemetry();
            render();
          },
        });
        mount(screen.element, 'live');
        live = screen;
      }
      live.update(state, sensorState ?? sensors?.current ?? EMPTY_SENSORS);
      return;
    }

    showShare(state);
  };

  const showShare = (state: SessionState): void => {
    if (mounted === 'share' && share) {
      share.update(state);
      return;
    }

    const screen = shareScreen({
      code,
      state,
      host,
      ready: () => sensorsReady,
      onSetName: (name) => {
        socket?.sendName(name);
        model?.setSelfName(name);
      },
      onSetUp: () => showOnboarding(),
      onChooseRally: () => showRallyPicker(),
    });
    mount(screen.element, 'share');
    share = screen;
  };

  /* --- the two taps ------------------------------------------------------ */

  const showOnboarding = (): void => {
    if (!sensors || disposed) return;
    stopOnboarding?.();

    const node = el('div', { class: 'session__onboarding' });
    const others = sessionState ? otherMembers(sessionState) : [];
    mount(node, 'onboarding');

    stopOnboarding = mountOnboarding(node, {
      sensors,
      peerName: others[0]?.name ?? null,
      // A host who is only setting themselves up early can back out; somebody about to be handed
      // an arrow cannot, because for them this *is* the next screen.
      allowLater: host && others.length === 0,
      onDone: (outcome) => {
        stopOnboarding?.();
        stopOnboarding = null;
        if (disposed) return;

        if (outcome !== 'later') {
          // Even a refusal continues: the screens say plainly what is missing, which is more
          // useful than a dead end, and the link in the bar still works either way.
          sensorsReady = true;
          sensors?.start();
          sensorState = sensors?.current ?? null;
        }
        mounted = 'none';
        render();
      },
    });
  };

  /* --- connecting -------------------------------------------------------- */

  const connect = (membership: Membership): void => {
    host = membership.created === true;

    const session = new SessionModel();
    model = session;

    const connection = new SessionSocket({
      code,
      participant: membership.participant,
      token: membership.token,
      onMessage: (message) => {
        session.apply(message);
        // A signal is an event, not state: it is handed straight to the screen and then it is
        // gone. Nothing about it is stored, here or anywhere else.
        if (message.type === 'signal') live?.onSignal(message.signal, message.name);
      },
      onStatus: (status) => session.setConnection(status),
      onEnd: (reason) => session.setEnded(reason),
    });
    socket = connection;

    bar = createSessionBar({
      code,
      onInvite: () => showInvite(),
      onExtend: () =>
        api
          .extendSession(code)
          .then((extended) => {
            session.setExpiry(extended.expiresAt, extended.extensionsLeft);
            refreshMembershipExpiry(code, extended.expiresAt);
            // No toast: the clock jumps by an hour and the warning row disappears, which is the
            // confirmation. A message saying so as well would be a message about a number that
            // is already on screen.
            return true;
          })
          .catch((error: unknown) => {
            if (error instanceof ApiError && error.isTerminal) {
              session.setEnded('ended');
              return false;
            }
            return false;
          }),
    });

    /*
     * Built now, started later.
     *
     * `hub.start()` calls `watchPosition`, which fires the browser's own permission prompt — so
     * starting it here would put the native dialog on screen before the sentence explaining why,
     * which is exactly what the onboarding screens exist to prevent.
     */
    const hub = new SensorHub({
      forceKeyboard: simulate,
      onChange: (state) => {
        sensorState = state;
        // Every usable fix goes to the peer; the socket throttles to one a second.
        if (state.fix?.usable) {
          connection.sendPosition({
            lat: state.fix.lat,
            lng: state.fix.lng,
            acc: state.fix.accuracy,
            heading: state.heading,
            ts: state.fix.at,
            alt: state.fix.altitude,
            crs: state.fix.course,
          });
          metrics.recordOwnFix(state.fix.accuracy, state.headingAvailable, Date.now());

          // Fold the separation into the aggregates, against whoever is nearest. The two
          // positions are used for one subtraction and then forgotten — nothing about where
          // anybody was survives.
          const nearest = sessionState
            ? defaultFocus(sessionState, { lat: state.fix.lat, lng: state.fix.lng })
            : null;
          if (nearest?.frame) {
            metrics.recordSeparation(
              { lat: state.fix.lat, lng: state.fix.lng },
              { lat: nearest.frame.lat, lng: nearest.frame.lng },
              Date.now(),
            );
          }

          // The simulated peer walks towards wherever we actually are.
          simPeer?.setTarget({ lat: state.fix.lat, lng: state.fix.lng });
        }
        render();
      },
    });
    sensors = hub;

    unsubscribe = session.subscribe((state) => {
      sessionState = state;
      render();
    });

    session.start();
    connection.connect();

    // The fake peer claims the free slot over the real REST endpoint and sends its positions
    // down a real socket, so everything downstream is exercised rather than short-circuited.
    if (sim) {
      void startSimulatedPeer({ code, options: sim, log: (message) => console.warn(message) }).then(
        (peer) => {
          if (disposed || !peer) {
            peer?.stop();
            return;
          }
          simPeer = peer;
          const fix = sensors?.current.fix;
          if (fix) peer.setTarget({ lat: fix.lat, lng: fix.lng });
        },
      );
    }
  };

  /* --- deciding what to show ------------------------------------------- */

  const resolve = async (): Promise<void> => {
    const membership = recallMembership(code);
    if (membership) {
      connect(membership);
      return;
    }

    try {
      const status = await api.getSession(code);
      if (disposed) return;

      if (status.full) {
        mount(fullScreen(), 'terminal');
        return;
      }

      mount(
        joinScreen({
          code,
          status,
          onJoined: () => void resolve(),
          onUnavailable: (error) =>
            mount(error.code === 'session_full' ? fullScreen() : endedScreen(), 'terminal'),
        }),
        'terminal',
      );
    } catch (error) {
      if (disposed) return;

      if (error instanceof ApiError && error.isTerminal) {
        mount(endedScreen(), 'terminal');
        return;
      }

      mount(
        errorScreen({
          title: 'Cannot reach Beeline',
          body: 'Check your connection — the session is still there.',
          actionLabel: 'Try again',
          onAction: () => void resolve(),
        }),
        'terminal',
      );
    }
  };

  showLoading('Opening the session…');
  void resolve();

  return () => {
    disposed = true;
    document.removeEventListener('visibilitychange', onHidden);
    reportTelemetry();
    teardownLive();
  };
};

/** What the live screen reads before the first sensor publish. Empty, and honest about it. */
const EMPTY_SENSORS: SensorState = {
  fix: null,
  heading: null,
  headingAvailable: false,
  headingAbsolute: false,
  headingSource: null,
  headingBasis: 'compass',
  headingSuspect: false,
  locationError: null,
  simulated: false,
};
